import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useItems, useTasks, useWorkflowStages, useActiveUsers, useProjects, useStaleItemsReport } from '../hooks/useQueries';
import { useIsMobile } from '../hooks/useIsMobile';
import PageHeader from '../components/Layout/PageHeader';
import LoadingSpinner from '../components/Popup/LoadingSpinner';
import Toast from '../components/Popup/Toast';
import Select from 'react-select';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Line, Pie } from 'react-chartjs-2';
import { formatItemRef } from '../utils/itemIdFormat';
import './reports.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

export default function ReportsPage() {
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [selectedProject, setSelectedProject] = useState(null);
  const [activeItemsOnly, setActiveItemsOnly] = useState(true);
  const [staleSearch, setStaleSearch] = useState('');

  const isMobile = useIsMobile();

  // Fetch data using existing hooks
  const { data: items = [], isLoading: itemsLoading, error: itemsError } = useItems();
  const { data: tasks = [], isLoading: tasksLoading, error: tasksError } = useTasks();
  const { data: workflowStages = [], isLoading: stagesLoading, error: stagesError } = useWorkflowStages();
  const { data: users = [], isLoading: usersLoading, error: usersError } = useActiveUsers();
  const { data: projects = [], isLoading: projectsLoading, error: projectsError } = useProjects();

  const staleDays = 10;
  const {
    data: staleReport,
    isLoading: staleLoading,
    error: staleError,
  } = useStaleItemsReport(
    {
      days: staleDays,
      projectId: selectedProject?.value ?? null,
      openOnly: activeItemsOnly,
    },
    {}
  );

  const staleItems = useMemo(() => {
    return Array.isArray(staleReport?.items) ? staleReport.items : [];
  }, [staleReport?.items]);

  const filteredStaleItems = useMemo(() => {
    const query = String(staleSearch || '').trim().toLowerCase();
    if (!query) return staleItems;

    const normalized = query.startsWith('#') ? query.slice(1) : query;

    return staleItems.filter((item) => {
      const id = String(item?.id ?? '');
      const title = String(item?.title ?? '');
      const description = String(item?.description ?? '');
      const stage = String(item?.current_stage_name ?? '');

      return (
        id.includes(normalized) ||
        title.toLowerCase().includes(query) ||
        description.toLowerCase().includes(query) ||
        stage.toLowerCase().includes(query)
      );
    });
  }, [staleItems, staleSearch]);

  const handleCloseToast = () => {
    setToast({ show: false, message: '', type: 'success' });
  };

  // Filter data based on selected filters
  const filteredItems = useMemo(() => {
    let filtered = items;
    
    // Filter by project
    if (selectedProject) {
      filtered = filtered.filter(item => item.project_id === selectedProject.value);
    }
    
    // Filter by open items only
    if (activeItemsOnly) {
      // Assuming active items are those not in a final/closed stage
      // You may need to adjust this logic based on your workflow stage definitions
      const finalStageNames = ['Closed', 'Completed', 'Cancelled', 'Done', 'Finished'];
      filtered = filtered.filter(item => 
        !finalStageNames.some(stageName => 
          item.current_stage_name && item.current_stage_name.toLowerCase().includes(stageName.toLowerCase())
        )
      );
    }
    
    return filtered;
  }, [items, selectedProject, activeItemsOnly]);

  // Filter tasks based on their parent items
  const filteredTasks = useMemo(() => {
    let filtered = tasks;
    
    // Filter by project (through parent items)
    if (selectedProject) {
      const projectItemIds = filteredItems.map(item => item.id);
      filtered = filtered.filter(task => projectItemIds.includes(task.parent_id));
    }
    
    return filtered;
  }, [tasks, filteredItems, selectedProject]);

  // Project options for dropdown
  const projectOptions = useMemo(() => [
    ...projects.map(project => ({
      value: project.id,
      label: project.name
    }))
  ], [projects]);

  // Calculate chart data
  const itemsByStage = useMemo(() => {
    if (!filteredItems.length || !workflowStages.length) return { labels: [], data: [] };

    // Sort workflow stages by stage_order first
    const sortedStages = [...workflowStages].sort((a, b) => a.stage_order - b.stage_order);

    // Create a map of stage counts using current_stage ID
    const stageCounts = {};
    
    // Initialize all stages with 0 using stage names in proper order
    sortedStages.forEach(stage => {
      stageCounts[stage.stage_name] = 0;
    });

    // Count items by stage using current_stage ID to lookup stage name
    filteredItems.forEach(item => {
      if (item.current_stage) {
        // Find the corresponding workflow stage by ID
        const stage = workflowStages.find(ws => ws.id === item.current_stage);
        if (stage && stageCounts.hasOwnProperty(stage.stage_name)) {
          stageCounts[stage.stage_name]++;
        }
      }
    });

    // Return in stage_order sequence
    const labels = sortedStages.map(stage => stage.stage_name);
    const data = labels.map(label => stageCounts[label]);

    return { labels, data };
  }, [filteredItems, workflowStages]);

  const openTasksByDay = useMemo(() => {
    if (!filteredTasks.length) return { labels: [], data: [] };

    // Generate date labels for the last 30 days
    const labels = [];
    const data = [];
    const today = new Date();
    
    for (let i = 29; i >= 0; i--) {
      const date = new Date();
      date.setDate(today.getDate() - i);
      const dateStr = date.toISOString().split('T')[0]; // YYYY-MM-DD format
      labels.push(date.toLocaleDateString());
      
      // Count tasks that were open on this specific date
      // A task is considered "open" on a date if:
      // 1. It was created on or before this date (created_on <= date)
      // 2. It was NOT completed on or before this date (completed_on is NULL or completed_on > date)
      const openTasksOnDate = filteredTasks.filter(task => {
        const createdDate = new Date(task.created_on);
        const taskCreatedStr = createdDate.toISOString().split('T')[0];
        
        // Task must exist on this date (created on or before)
        if (taskCreatedStr > dateStr) {
          return false;
        }
        
        // If task has no completion date, it's still open
        if (!task.completed_on) {
          return true;
        }
        
        // If task was completed after this date, it was still open on this date
        const completedDate = new Date(task.completed_on);
        const completedDateStr = completedDate.toISOString().split('T')[0];
        return completedDateStr > dateStr;
      });
      
      data.push(openTasksOnDate.length);
    }
    
    return { labels, data };
  }, [filteredTasks]);

  const tasksByStatus = useMemo(() => {
    if (!filteredTasks.length) return { labels: [], data: [] };

    // Count tasks by status
    const statusCounts = {};
    filteredTasks.forEach(task => {
      const status = task.status || 'Unknown';
      statusCounts[status] = (statusCounts[status] || 0) + 1;
    });

    return {
      labels: Object.keys(statusCounts),
      data: Object.values(statusCounts)
    };
  }, [filteredTasks]);

  const averageTimeInStage = useMemo(() => {
    if (!filteredItems.length || !workflowStages.length) return { labels: [], data: [] };

    // Sort workflow stages by stage_order first
    const sortedStages = [...workflowStages].sort((a, b) => a.stage_order - b.stage_order);

    // Calculate average time spent in each stage
    const stageTimeTotals = {}; // { stageName: { totalTime: 0, itemCount: 0 } }
    
    // Initialize all stages - use both stage_name and id for lookup
    const stageNameMap = {}; // Map stage names to normalized names
    sortedStages.forEach(stage => {
      const stageName = stage.stage_name || stage.name || `Stage ${stage.id}`;
      stageTimeTotals[stageName] = { totalTime: 0, itemCount: 0 };
      stageNameMap[stage.id] = stageName;
      stageNameMap[stageName] = stageName;
    });

    filteredItems.forEach((item, index) => {

      let history = item.stage_history;
      
      // Handle case where stage_history might be a string that needs parsing
      if (typeof history === 'string') {
        try {
          history = JSON.parse(history);
        } catch (e) {
          console.warn(`Failed to parse stage_history for item ${item.id}:`, e);
          return;
        }
      }
      
      if (!history || !Array.isArray(history) || history.length === 0) {
        return;
      }
      
      const sortedHistory = [...history].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      
      for (let i = 0; i < sortedHistory.length - 1; i++) {
        const currentEntry = sortedHistory[i];
        const nextEntry = sortedHistory[i + 1];
        
        const startTime = new Date(currentEntry.timestamp);
        const endTime = new Date(nextEntry.timestamp);
        const timeSpent = endTime - startTime; // milliseconds
        
        // Try to find the stage name using different possible mappings
        let stageName = stageNameMap[currentEntry.stage] || 
                       stageNameMap[currentEntry.stage_id] ||
                       currentEntry.stage;
        
        if (timeSpent > 0 && stageName && stageTimeTotals[stageName]) {
          stageTimeTotals[stageName].totalTime += timeSpent;
          stageTimeTotals[stageName].itemCount += 1;
        }
      }
      
      // Handle current stage (still ongoing)
      if (sortedHistory.length > 0) {
        const lastEntry = sortedHistory[sortedHistory.length - 1];
        const now = new Date();
        const startTime = new Date(lastEntry.timestamp);
        const timeSpent = now - startTime;
        
        // Try to find the stage name using different possible mappings
        let stageName = stageNameMap[lastEntry.stage] || 
                       stageNameMap[lastEntry.stage_id] ||
                       lastEntry.stage;
        
        if (timeSpent > 0 && stageName && stageTimeTotals[stageName]) {
          stageTimeTotals[stageName].totalTime += timeSpent;
          stageTimeTotals[stageName].itemCount += 1;
        }
      }
    });

    // Calculate averages and convert to days - use sorted stages for consistent order
    const labels = [];
    const data = [];
    
    sortedStages.forEach(stage => {
      const stageName = stage.stage_name || stage.name || `Stage ${stage.id}`;
      const stageData = stageTimeTotals[stageName];
      labels.push(stageName);
      
      if (stageData && stageData.itemCount > 0) {
        const averageMs = stageData.totalTime / stageData.itemCount;
        const averageDays = averageMs / (1000 * 60 * 60 * 24); // Convert to days
        data.push(Math.round(averageDays * 10) / 10); // Round to 1 decimal place
      } else {
        data.push(0);
      }
    });

    return { labels, data };
  }, [filteredItems, workflowStages]);

  // Chart options
  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: 'Items by Workflow Stage',
        padding: {
          top: 0,
          bottom: 10,
        },
        font: {
          size: isMobile ? 14 : 16,
          weight: 'bold'
        }
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Workflow Stage',
          font: { size: isMobile ? 11 : 12 }
        },
        ticks: {
          maxRotation: isMobile ? 90 : 45,
          minRotation: isMobile ? 90 : 45,
          font: { size: isMobile ? 10 : 11 }
        }
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Number of Items',
          font: { size: isMobile ? 11 : 12 }
        },
        ticks: {
          stepSize: 1,
          font: { size: isMobile ? 10 : 11 }
        }
      },
    },
  };

  // Function to generate consistent colors for workflow stages
  const getStageColors = (stageLabels, workflowStages) => {
    const stageColorMap = {
      'Create': 'rgba(54, 162, 235, COLOR_ALPHA)', // Blue
      'Endorse': 'rgba(255, 99, 132, COLOR_ALPHA)', // Red
      'Plan': 'rgba(255, 205, 86, COLOR_ALPHA)', // Yellow
      'Review': 'rgba(126, 28, 28, COLOR_ALPHA)', // Brand red - chart.js paints to canvas and cannot resolve var()
      'Finalize': 'rgba(153, 102, 255, COLOR_ALPHA)', // Purple
      'Done': 'rgba(255, 159, 64, COLOR_ALPHA)', // Orange
    };
    
    // Fallback colors for any stages not in the map
    const fallbackColors = [
      'rgba(199, 199, 199, COLOR_ALPHA)', // Gray
      'rgba(83, 102, 255, COLOR_ALPHA)', // Blue variant
      'rgba(255, 159, 179, COLOR_ALPHA)', // Pink
      'rgba(126, 28, 28, COLOR_ALPHA)', // Brand red - chart.js paints to canvas and cannot resolve var()
      'rgba(153, 102, 255, COLOR_ALPHA)', // Purple
      'rgba(159, 255, 159, COLOR_ALPHA)', // Light Green
      'rgba(255, 159, 64, COLOR_ALPHA)', //Orange
    ];
    
    const backgroundColors = [];
    const borderColors = [];
    
    stageLabels.forEach((label, index) => {
      let color = stageColorMap[label];
      if (!color) {
        // Use fallback color if stage not found in map
        const fallbackIndex = index % fallbackColors.length;
        color = fallbackColors[fallbackIndex];
      }
      
      backgroundColors.push(color.replace('COLOR_ALPHA', '0.8'));
      borderColors.push(color.replace('COLOR_ALPHA', '1'));
    });
    
    return { backgroundColors, borderColors };
  };

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: 'Open Tasks Over Time (Last 30 Days)',
        padding: {
          top: 0,
          bottom: 10,
        },
        font: {
          size: isMobile ? 14 : 16,
          weight: 'bold'
        }
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Date',
          font: { size: isMobile ? 11 : 12 }
        },
        ticks: {
          maxTicksLimit: isMobile ? 5 : 8, // Show fewer labels to avoid crowding
          font: { size: isMobile ? 10 : 11 }
        }
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Number of Open Tasks',
          font: { size: isMobile ? 11 : 12 }
        },
        ticks: {
          stepSize: 1,
          font: { size: isMobile ? 10 : 11 }
        }
      },
    },
  };

  const barChartData = {
    labels: itemsByStage.labels,
    datasets: [
      {
        data: itemsByStage.data,
        ...(() => {
          const { backgroundColors, borderColors } = getStageColors(itemsByStage.labels, workflowStages);
          return { backgroundColor: backgroundColors, borderColor: borderColors };
        })(),
        borderWidth: 1,
      },
    ],
  };

  const lineChartData = {
    labels: openTasksByDay.labels,
    datasets: [
      {
        data: openTasksByDay.data,
        borderColor: 'rgba(54, 162, 235, 1)',
        backgroundColor: 'rgba(54, 162, 235, 0.1)',
        borderWidth: 2,
        fill: true,
        tension: 0.1,
      },
    ],
  };

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          font: {
            size: isMobile ? 10 : 12,
          },
        },
      },
      title: {
        display: true,
        text: 'Tasks by Status',
        padding: {
          top: 0,
          bottom: 10,
        },
        font: {
          size: isMobile ? 14 : 16,
          weight: 'bold'
        }
      },
    },
  };

  const avgTimeChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: 'Average Time by Stage (Days)',
        padding: {
          top: 0,
          bottom: 10,
        },
        font: {
          size: isMobile ? 14 : 16,
          weight: 'bold'
        }
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Workflow Stage',
          font: { size: isMobile ? 11 : 12 }
        },
        ticks: {
          maxRotation: isMobile ? 90 : 45,
          minRotation: isMobile ? 90 : 45,
          font: { size: isMobile ? 10 : 11 }
        }
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Average Days',
          font: { size: isMobile ? 11 : 12 }
        },
        ticks: {
          font: { size: isMobile ? 10 : 11 }
        }
      },
    },
  };

  const pieChartData = {
    labels: tasksByStatus.labels,
    datasets: [
      {
        data: tasksByStatus.data,
        backgroundColor: [
          'rgba(255, 99, 132, 0.8)',
          'rgba(54, 162, 235, 0.8)',
          'rgba(255, 205, 86, 0.8)',
          'rgba(75, 192, 192, 0.8)',
          'rgba(153, 102, 255, 0.8)',
          'rgba(255, 159, 64, 0.8)',
        ],
        borderColor: [
          'rgba(255, 99, 132, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(255, 205, 86, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(153, 102, 255, 1)',
          'rgba(255, 159, 64, 1)',
        ],
        borderWidth: 1,
      },
    ],
  };

  const avgTimeChartData = {
    labels: averageTimeInStage.labels,
    datasets: [
      {
        data: averageTimeInStage.data,
        ...(() => {
          const { backgroundColors, borderColors } = getStageColors(averageTimeInStage.labels, workflowStages);
          return { backgroundColor: backgroundColors, borderColor: borderColors };
        })(),
        borderWidth: 1,
      },
    ],
  };

  const isLoading = itemsLoading || tasksLoading || stagesLoading || usersLoading || projectsLoading;
  const hasError = itemsError || tasksError || stagesError || usersError || projectsError;

  if (isLoading) {
    return <LoadingSpinner message="Loading reports..." />;
  }

  if (hasError) {
    return (
      <div className="container-fluid">
        <PageHeader title="Reports" totalCount={0} itemLabel="reports" isMobile={isMobile} />
        <div className="alert alert-danger">
          Failed to load data: {itemsError?.message || tasksError?.message || stagesError?.message || usersError?.message || projectsError?.message}
        </div>
        <Toast
          show={toast.show}
          onClose={handleCloseToast}
          message={toast.message}
          type={toast.type}
        />
      </div>
    );
  }

  return (
    <div className="reports-page">
      <PageHeader 
        title="Reports"
        isMobile={isMobile}
      >
        {/* Filter Controls */}
        <div className="report-filters" style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '15px', alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'space-between', width: '100%' }}>
          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '15px', alignItems: isMobile ? 'stretch' : 'center', width: isMobile ? '100%' : 'auto' }}>
            <div style={{ width: isMobile ? '100%' : '250px'}}>
              <Select
                value={selectedProject}
                onChange={setSelectedProject}
                options={projectOptions}
                placeholder="All Projects"
                isClearable
                className="project-filter"
              />
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontWeight: '500', cursor: 'pointer', minHeight: '44px', padding: isMobile ? '8px' : undefined }}>
              <input
                type="checkbox"
                checked={activeItemsOnly}
                onChange={(e) => setActiveItemsOnly(e.target.checked)}
                style={{ marginRight: '5px' }}
              />
              Open Items Only
            </label>
          </div>
          <span className="status-badge success" style={{
            background: 'var(--nsi-brand)',
            color: '#fff',
            borderRadius: '20px',
            padding: '6px 16px',
            fontWeight: '600',
            fontSize: '15px',
            display: 'inline-block',
          }}>
            {filteredItems.length + filteredTasks.length} items
          </span>
        </div>
      </PageHeader>
      
      {/* Big Numbers Section */}
      <div className="reports-metrics">
        <div className="metric-card">
          <div className="metric-icon">
            <i className="bi bi-clipboard-check"></i>
          </div>
          <div className="metric-content">
            <div className="metric-number">{filteredItems.length}</div>
            <div className="metric-label">Total Items</div>
          </div>
        </div>
        
        <div className="metric-card">
          <div className="metric-icon">
            <i className="bi bi-list-task"></i>
          </div>
          <div className="metric-content">
            <div className="metric-number">{filteredTasks.length}</div>
            <div className="metric-label">Total Tasks</div>
          </div>
        </div>
        
        <div className="metric-card">
          <div className="metric-icon">
            <i className="bi bi-people-fill"></i>
          </div>
          <div className="metric-content">
            <div className="metric-number">{users.length}</div>
            <div className="metric-label">Total RIIMS Users</div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="reports-charts">
        <div className="chart-container">
          <div className="chart-wrapper">
            <Bar data={barChartData} options={barChartOptions} />
          </div>
        </div>
        
        <div className="chart-container">
          <div className="chart-wrapper">
            <Line data={lineChartData} options={lineChartOptions} />
          </div>
        </div>
        
        <div className="chart-container">
          <div className="chart-wrapper">
            <Pie data={pieChartData} options={pieChartOptions} />
          </div>
        </div>
        
        <div className="chart-container">
          <div className="chart-wrapper">
            <Bar data={avgTimeChartData} options={avgTimeChartOptions} />
          </div>
        </div>

        <div className="chart-container">
          <div className="chart-wrapper stale-items-wrapper">
            <div className="stale-items-header">
              <div>
                <div className="stale-items-title">Stale Items</div>
                <div className="stale-items-subtitle">
                  No activity in the last {staleDays} days (item updates, linked task updates/completions, and task comments)
                </div>
              </div>
              <div className="stale-items-controls">
                <input
                  type="search"
                  className="form-control form-control-sm stale-items-search"
                  placeholder="Search items…"
                  value={staleSearch}
                  onChange={(e) => setStaleSearch(e.target.value)}
                />
                <div className="stale-items-count">
                  {staleLoading
                    ? 'Loading…'
                    : staleSearch.trim()
                      ? `${filteredStaleItems.length} of ${staleItems.length}`
                      : `${staleItems.length} item${staleItems.length === 1 ? '' : 's'}`}
                </div>
              </div>
            </div>

            {staleError && (
              <div className="alert alert-warning" style={{ marginTop: 12 }}>
                Failed to load stale items: {staleError.message}
              </div>
            )}

            {!staleError && (
              <div className="stale-items-list" role="list">
                {staleLoading && (
                  <div className="stale-items-empty text-muted">
                    Loading stale items…
                  </div>
                )}

                {!staleLoading && filteredStaleItems.map((item) => {
                  const lastActivity = item.last_activity ? new Date(item.last_activity) : null;
                  const description = item.description ? String(item.description) : '';
                  const truncatedDescription = description.length > 180 ? `${description.slice(0, 180)}…` : description;
                  const title = item.title ? String(item.title) : `Item ${formatItemRef(item.id)}`;

                  return (
                    <div key={item.id} className="stale-item" role="listitem">
                      <Link to={`/items/${item.id}?source=reports`} className="stale-item-title">
                        {title}
                      </Link>

                      {truncatedDescription && (
                        <div className="stale-item-description">
                          {truncatedDescription}
                        </div>
                      )}

                      <div className="stale-item-meta">
                        <span className="stale-item-meta-chip">{formatItemRef(item.id)}</span>
                        <span className="stale-item-meta-chip">
                          Stage: {item.current_stage_name || '—'}
                        </span>
                        <span className="stale-item-meta-chip">
                          Last Activity: {lastActivity ? lastActivity.toLocaleDateString() : '—'}
                        </span>
                      </div>
                    </div>
                  );
                })}

                {!staleLoading && filteredStaleItems.length === 0 && (
                  <div className="stale-items-empty text-muted">
                    {staleSearch.trim()
                      ? 'No items match your search.'
                      : 'No stale items found for the selected filters.'}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <Toast
        show={toast.show}
        onClose={handleCloseToast}
        message={toast.message}
        type={toast.type}
      />
    </div>
  );
}